import { clsx } from "clsx";
import { useId } from "react";
import styles from "./SensitivitySlider.module.css";

/**
 * Sensitivity slider for the Noise pod's gain control.
 *
 * Pure-native `<input type="range">` with `accent-color` for theming —
 * the browser handles the track, fill, thumb, keyboard interaction,
 * and ARIA attributes. Designed as a thin replacement for the prior
 * nessie/radix Slider which is being retired alongside the rest of
 * nessie in toolkit.
 *
 * Trade-off acknowledged at design time: the browser-default thumb +
 * rail sizes are smaller than the radix visual. If a heavier visual
 * is needed we can revisit when DDS ships a Slider primitive.
 *
 * `theme="light"` is for use over a dark backdrop (Noise display side);
 * the label + plus/minus glyphs invert to white. Default `dark` is for
 * the phone-side modal which sits on a light surface.
 *
 * `label` is the visible label above the rail (terse, e.g.
 * "Sensitivity"). `ariaLabel` is the announced label for screen readers
 * (descriptive, e.g. "Noise sensitivity range"). When set, `ariaLabel`
 * takes precedence in the a11y tree — the visible `<label>` becomes
 * decoration for sighted users only. Pass only `label` when the two
 * should be identical.
 */
type SensitivitySliderProps = {
  label: string;
  "aria-label"?: string;
  value?: number;
  defaultValue?: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  theme?: "light" | "dark";
};

export function SensitivitySlider({
  label,
  "aria-label": ariaLabel,
  value,
  defaultValue,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  theme = "dark",
}: SensitivitySliderProps) {
  const id = useId();
  return (
    <div className={clsx(styles.wrapper, theme === "light" && styles.light)}>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      <div className={styles.row}>
        <span className={styles.icon} aria-hidden="true">
          −
        </span>
        <input
          id={id}
          type="range"
          aria-label={ariaLabel ?? label}
          value={value}
          defaultValue={defaultValue}
          min={min}
          max={max}
          step={step}
          onChange={(e) => onChange(Number(e.target.value))}
          className={styles.input}
        />
        <span className={styles.icon} aria-hidden="true">
          +
        </span>
      </div>
    </div>
  );
}
